import { defineStore } from 'pinia';
import BlockAPI from '@/store/api/block';
import {
  Block,
  Crop,
  CropVariety,
  AssignCrop,
  GeomBlock,
  BlocksWithFruitCounts,
  SimpleBlock,
  PaginatedBlocks,
  CropType,
  CropParameters,
  emptyCropParameters,
  AssignCorrectionFactor,
  BlocksWithSummary
} from '@/store/models/block';
import { QTablePagination } from '@/store/models/meta';
import { emptyPage } from '@/store/models/page';
import { latestRequest } from '@/utils/latest-request';
import { buildQuery } from '@/utils/query';

interface BlockQuery {
  block?: number;
  farm?: number;
  page?: number;
  pageSize?: number;
  search?: string;
  ordering?: string;
  startDate?: string;
  endDate?: string;
  crop?: number | null;
  cropSeasonCalendarsSeasons?: number | null;
  seasonStartDate?: string;
  seasonEndDate?: string;
  method?: string;
}

function getBlockQuery(params?: BlockQuery) {
  return buildQuery({
    id: params?.block,
    farm: params?.farm,
    crop: params?.crop,
    page: params?.page,
    page_size: params?.pageSize,
    search: params?.search,
    ordering: params?.ordering,
    start_date: params?.startDate,
    end_date: params?.endDate,
    crop__season_calendars__seasons: params?.cropSeasonCalendarsSeasons,
    season_start_date: params?.seasonStartDate,
    season_end_date: params?.seasonEndDate,
    method: params?.method
  });
}

// The forms ask for a farm's blocks on each farm pick; the last pick's answer
// is kept, whichever lands last.
const geomBlocksRequest = latestRequest();

export const useBlockStore = defineStore('block', {
  state: () => ({
    SimpleBlocks: [] as SimpleBlock[],
    SeasonWiseSimpleBlocks: [] as SimpleBlock[],
    Blocks: emptyPage<PaginatedBlocks>(),
    BlocksWithFruitCount: [] as BlocksWithFruitCounts[],
    BlocksWithManualFruitCount: [] as BlocksWithFruitCounts[],
    BlocksWithMVFruitCount: [] as BlocksWithFruitCounts[],
    BlocksWithAllFruitCount: [] as BlocksWithFruitCounts[],
    GeomBlocks: [] as GeomBlock[],
    emptyBlock: {} as GeomBlock,
    AddEditBlock: {} as GeomBlock,
    BlocksWithSummary: [] as BlocksWithSummary[],
    BlockTablePagination: {
      sortBy: 'id',
      descending: true,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 1
    } as QTablePagination,
    Crops: [] as Crop[],
    FarmCrops: [] as Crop[],
    emptyCrop: {} as Crop,
    AddEditCrop: {} as Crop,
    AddEditCropType: {} as CropType,
    emptyCropType: {} as CropType,
    AddEditCropVariety: {} as CropVariety,
    emptyCropVariety: {} as CropVariety,
    CropTypes: [] as CropType[],
    CropVarieties: [] as CropVariety[],
    CropParameters: emptyCropParameters() as CropParameters,
    SeasonCrops: [] as Crop[],
    ImportBlockDialogVisible: false,
    AssignCorrectionFactorDialog: false,
    PackHouseBatchUploadDialog: false
  }),
  actions: {
    setImportBlockDialogVisible(visible: boolean) {
      this.ImportBlockDialogVisible = visible;
    },
    setAssignCorrectionFactorDialog(visible: boolean) {
      this.AssignCorrectionFactorDialog = visible;
    },
    setPackHouseBatchUploadDialog(visible: boolean) {
      this.PackHouseBatchUploadDialog = visible;
    },
    setBlockTablePagination(data: QTablePagination) {
      this.BlockTablePagination = data;
    },
    setAddEditBlock(block: Block) {
      this.AddEditBlock = block as GeomBlock;
    },
    async getSimpleBlocks(params?: BlockQuery) {
      this.SimpleBlocks = await BlockAPI.getSimpleBlocks(getBlockQuery(params));
    },
    async getSeasonWiseSimpleBlocks(params?: BlockQuery) {
      this.SeasonWiseSimpleBlocks = await BlockAPI.getSimpleBlocks(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocks(params?: BlockQuery) {
      this.Blocks = await BlockAPI.getAllMyBlocks(getBlockQuery(params));
    },
    async getAllMyBlocksWithFruitCounts(params?: BlockQuery) {
      this.BlocksWithFruitCount = await BlockAPI.getAllMyBlocksWithFruitCounts(
        getBlockQuery(params)
      );
    },
    async getBlocksWithManualFruitCounts(params?: BlockQuery) {
      this.BlocksWithManualFruitCount = await BlockAPI.getAllMyBlocksWithFruitCounts(
        getBlockQuery({ ...params, method: 'M' })
      );
    },
    async getBlocksWithMVFruitCounts(params?: BlockQuery) {
      this.BlocksWithMVFruitCount = await BlockAPI.getAllMyBlocksWithFruitCounts(
        getBlockQuery({ ...params, method: 'MV' })
      );
    },
    async getAllMyBlocksWithOnlyFruitCounts(params?: BlockQuery) {
      this.BlocksWithAllFruitCount = await BlockAPI.getAllMyBlocksWithOnlyFruitCounts(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocksWithAvgDM(params?: BlockQuery) {
      this.BlocksWithAllFruitCount = await BlockAPI.getAllMyBlocksWithAvgDM(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocksWithFloweringCount(params?: BlockQuery) {
      this.BlocksWithAllFruitCount = await BlockAPI.getAllMyBlocksWithFloweringCount(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocksWithFruitSize(params?: BlockQuery) {
      this.BlocksWithAllFruitCount = await BlockAPI.getAllMyBlocksWithFruitSize(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocksSummary(params?: BlockQuery) {
      this.BlocksWithSummary = await BlockAPI.getAllMyBlocksSummary(
        getBlockQuery(params)
      );
    },
    async getAllMyBlocksWithGeom(params?: BlockQuery) {
      const isLatest = geomBlocksRequest();
      const blocks = await BlockAPI.getAllMyBlocksWithGeom(getBlockQuery(params));
      if (isLatest()) this.GeomBlocks = blocks;
    },
    clearAllMyBlocksWithGeom() {
      this.GeomBlocks = [];
    },
    async getBlockById(id: number) {
      this.AddEditBlock = await BlockAPI.getBlockById(id);
    },
    async getMapFeatureBlockById(params: {
      id: number;
      startDate: string;
      endDate: string;
      seasonStartDate?: string;
      seasonEndDate?: string;
      featureType?: string;
    }) {
      let query = `?start_date=${params.startDate}&end_date=${params.endDate}`;
      // The page's dates: the day the block's dry matter is grown to (FMW-622).
      if (params.seasonStartDate && params.seasonEndDate) {
        query += `&season_start_date=${params.seasonStartDate}&season_end_date=${params.seasonEndDate}`;
      }
      if (params.featureType) {
        query += `&feature_type=${params.featureType}`;
      }
      return await BlockAPI.getMapFeatureBlockById(params.id, query);
    },
    async importBlocks(params: { data: FormData }) {
      return await BlockAPI.importBlocks(params.data);
    },
    async exportFarmBlocks(farm: number) {
      return await BlockAPI.exportFarmBlocks(farm);
    },
    async exportBlock(id: number) {
      return await BlockAPI.exportBlock(id);
    },
    async exportBlocks(params: { farm: number; id?: number }) {
      const query = buildQuery({
        block__farm: params?.farm,
        id: params?.id
      });
      return await BlockAPI.exportBlocks(query);
    },
    async createBlock(block: Block) {
      const addedBlock = await BlockAPI.createBlock(block);
      if (this.Blocks.results) {
        this.Blocks.results.push(addedBlock);
      }
      this.AddEditBlock = addedBlock;
    },
    async updateBlock(block: Block) {
      const updatedBlock = await BlockAPI.updateBlock(block);
      if (this.Blocks.results) {
        const idx = this.Blocks.results.findIndex(x => x.id == block.id);
        if (idx !== -1) {
          this.Blocks.results.splice(idx, 1, updatedBlock);
        }
      }
      this.AddEditBlock = updatedBlock;
    },
    async deleteBlock(id: number) {
      await BlockAPI.deleteBlock(id);
      if (this.Blocks.results) {
        const idx = this.Blocks.results.findIndex(x => x.id == id);
        if (idx !== -1) {
          this.Blocks.results.splice(idx, 1);
        }
      }
      this.AddEditBlock = JSON.parse(JSON.stringify(this.emptyBlock));
    },
    async getAllCropTypes() {
      this.CropTypes = await BlockAPI.getAllCropTypes();
    },
    async getAllCropVarieties() {
      this.CropVarieties = await BlockAPI.getAllCropVarieties();
    },
    setAddEditCrop(crop: Crop) {
      this.AddEditCrop = crop;
    },
    setAddEditCropType(cropType: CropType) {
      this.AddEditCropType = cropType;
    },
    setAddEditCropVariety(cropVariety: CropVariety) {
      this.AddEditCropVariety = cropVariety;
    },
    async getAllCrops() {
      this.Crops = await BlockAPI.getAllCrops();
    },
    async getCropById(id: number) {
      this.AddEditCrop = await BlockAPI.getCrop(id);
    },
    async getCropParameters() {
      this.CropParameters = await BlockAPI.getCropParameters();
    },
    async getFarmCrops(params: { farmId: number }) {
      let query = '';
      if (params && params.farmId) {
        query = `?of_farm=${params.farmId}`;
      }
      this.FarmCrops = await BlockAPI.getAllCrops(query);
    },
    async getFarmAllCrops(params: { farmId: number }) {
      let query = '';
      if (params && params.farmId) {
        query = `?of_farm=${params.farmId}`;
      }
      this.SeasonCrops = await BlockAPI.getAllCrops(query);
    },
    async createCrop(crop: Crop) {
      const addedCrop = await BlockAPI.createCrop(crop);
      this.Crops.push(addedCrop);
      this.AddEditCrop = addedCrop;
    },
    async updateCrop(block: Crop) {
      const updatedCrop = await BlockAPI.updateCrop(block);
      const idx = this.Crops.findIndex(x => x.id == block.id);
      if (idx !== -1) {
        this.Crops.splice(idx, 1, updatedCrop);
      }
      this.AddEditCrop = updatedCrop;
    },
    async deleteCrop(id: number) {
      await BlockAPI.deleteCrop(id);
      const idx = this.Crops.findIndex(x => x.id == id);
      if (idx !== -1) {
        this.Crops.splice(idx, 1);
      }
      this.AddEditCrop = JSON.parse(JSON.stringify(this.emptyCrop));
    },
    async assignCrop(assignCrop: AssignCrop) {
      return await BlockAPI.assignCrop(assignCrop);
    },
    async assignCorrectionFactor(assignCorrectionFactor: AssignCorrectionFactor) {
      return await BlockAPI.assignCorrectionFactor(assignCorrectionFactor);
    },
    async batchUploadPackHouse(params: { data: FormData }) {
      return await BlockAPI.batchUploadPackHouse(params.data);
    },
    async getCropTypeById(id: number) {
      this.AddEditCropType = await BlockAPI.getCropType(id);
    },
    async createCropType(cropType: CropType) {
      const addedCropType = await BlockAPI.createCropType(cropType);
      this.CropTypes.push(addedCropType);
      this.AddEditCropType = addedCropType;
    },
    async updateCropType(cropType: CropType) {
      const updatedCropType = await BlockAPI.updateCropType(cropType);
      const idx = this.CropTypes.findIndex(x => x.id == cropType.id);
      if (idx !== -1) {
        this.CropTypes.splice(idx, 1, updatedCropType);
      }
      this.AddEditCropType = updatedCropType;
    },
    async deleteCropType(id: number) {
      await BlockAPI.deleteCropType(id);
      const idx = this.CropTypes.findIndex(x => x.id == id);
      if (idx !== -1) {
        this.CropTypes.splice(idx, 1);
      }
      this.AddEditCropType = JSON.parse(JSON.stringify(this.emptyCropType));
    },
    async getCropVarietyById(id: number) {
      this.AddEditCropVariety = await BlockAPI.getCropVariety(id);
    },
    async createCropVariety(cropVariety: CropVariety) {
      const addedCropVariety = await BlockAPI.createCropVariety(cropVariety);
      this.CropVarieties.push(addedCropVariety);
      this.AddEditCropVariety = addedCropVariety;
    },
    async updateCropVariety(cropVariety: CropVariety) {
      const updatedCropVariety = await BlockAPI.updateCropVariety(cropVariety);
      const idx = this.CropVarieties.findIndex(x => x.id == cropVariety.id);
      if (idx !== -1) {
        this.CropVarieties.splice(idx, 1, updatedCropVariety);
      }
      this.AddEditCropVariety = updatedCropVariety;
    },
    async deleteCropVariety(id: number) {
      await BlockAPI.deleteCropVariety(id);
      const idx = this.CropVarieties.findIndex(x => x.id == id);
      if (idx !== -1) {
        this.CropVarieties.splice(idx, 1);
      }
      this.AddEditCropVariety = JSON.parse(
        JSON.stringify(this.emptyCropVariety)
      );
    }
  }
});

